import { FC, useState } from "react";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import locationIcon from "assets/images/svgs/locationIcon.svg";
import useStore from "hooks/useStore";
import { ROLES } from "utils/helpers/enums/roles.enum";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useNavigate } from "react-router-dom";
import { BookmarkFilled, BookmarkUnfilled } from "assets/images/svgs";
import useAttendee from "containers/attendee/useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import {
  customConfirmationPopup,
  followEventPopup,
  shareEventPopup,
} from "utils/helpers/common/alert-service";
import useAmbassador from "containers/ambassador/useHooks";
import SupportForExperience from "containers/ambassador/family-profile/components/support-for-experience-creation-link";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";

interface Props {
  event: EventDTO;
  showBookmark?: boolean;
  followBtn?: boolean;
  shareEvent?: string;
}

const EventCard: FC<Props> = ({
  event,
  showBookmark = true,
  followBtn,
  shareEvent,
}) => {
  const { getCurrentRole, getAmbassadorreferralCode } = useStore();
  const [showSupportForExperience, setShowSupportForExperience] =
    useState(false);
  const userRole = getCurrentRole();
  const { addToWishlist, removeFromWishlist } = useAttendee();
  const { postEventFollowUp, postJoinEvents } = useAmbassador();
  const refferalCode = getAmbassadorreferralCode();
  const [exp, setExp] = useState<EventDTO>(event);
  const navigate = useNavigate();

  const goToEventDetail = (id: number) => {
    let pathToNavigate = siteRoutes.experienceDetail;
    let queryString = `?id=${id}`;

    if (userRole === ROLES.AMBASSADOR) {
      pathToNavigate = siteRoutes.ambassadorExperiencesDetail;
    } else if (userRole === ROLES.ATTENDEE) {
      pathToNavigate = siteRoutes.experiencesDetailAttendee;
    } else if (userRole === ROLES.CHILD) {
      pathToNavigate = siteRoutes.minorExperiencesDetail;
    } else if (userRole === ROLES.RSVP) {
      pathToNavigate = siteRoutes.rsvpExperiencesDetail;
    } else if (
      userRole === ROLES.HOST &&
      event.eventStatus === EVENT_STATUS.DRAFT
    ) {
      // ✅ Only for HOST + DRAFT
      pathToNavigate = siteRoutes.createExperience;
      queryString = `?id=${event.id}&draft=true`;
    }

    navigate(`${pathToNavigate}${queryString}`);
  };

  const handleToggleWishlist = () => {
    exp.isWishlisted = !exp.isWishlisted;
    if (exp.isWishlisted) {
      addToWishlist(exp.id);
    } else {
      removeFromWishlist(exp.id);
    }
    setExp({ ...exp });
  };

  const handleFollowEvent = async () => {
    const result = await followEventPopup("Follow Event");

    if (result.isConfirmed) {
      postEventFollowUp(exp?.id, {});
    }
  };

  // const handleShareEvent = async (id: number) => {
  //   const result = await shareEventPopup("Sign Up Event");

  //   if (result.isConfirmed) {
  //     try {
  //       const response = await postJoinEvents(id, {});
  //       if (response?.status) {
  //         setShowSupportForExperience(true);
  //       }
  //     } catch (error) {
  //       console.error("Join event failed:", error);
  //     }
  //   }
  // };

  const handleShareEvent = async (id: number) => {
    setShowSupportForExperience(true);
  };

  // OPEN MODAL
  const handleShareClick = () => {
    setShowSupportForExperience(true);
  };
  const onClose = () => {
    setShowSupportForExperience(false);
  };

  const handleConfirm = () => {};

  // Helper function to get date info from workshop session
  const getDateInfo = () => {
    const session = exp?.workshopSessions?.[0];
    if (!session) return null;

    // If startDate, month, and year already exist (from DTO transformation), use them
    if (session.startDate && session.month && session.year) {
      return {
        day: session.startDate,
        month: session.month,
        year: session.year,
      };
    }

    // Otherwise, parse from startDateTime
    if (session.startDateTime) {
      try {
        const date = new Date(session.startDateTime);
        if (isNaN(date.getTime())) return null;

        const day = date.getDate().toString();
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear().toString();

        return { day, month, year };
      } catch (error) {
        return null;
      }
    }

    return null;
  };

  const dateInfo = getDateInfo();

  return (
    <div
      onClick={() => {
        if (followBtn === true) {
          handleFollowEvent();
        } else if (shareEvent === "true") {
          handleShareEvent(exp?.id);
        } else {
          goToEventDetail(exp.id);
        }
      }}
      className="p-3 bg-white rounded-xl border border-borderColor cursor-pointer"
    >
      <div className="relative">
        <img
          src={
            concatPathWithBackendUrl(
              exp.eventPicsUrl?.[0]?.fileDownloadUri || 
              exp.eventPics?.[0]?.fileDownloadUri || 
              exp.eventPicsUrl?.[0]?.name || 
              exp.eventPics?.[0]?.name || 
              ""
            )
          }
          alt={exp.title}
          className="w-full h-[160px] object-cover rounded-xl"
        />

        {dateInfo && (
          <div className="absolute top-2 left-2 bg-white text-center p-2 rounded-md">
            <span className="block text-base font-bold text-primaryBlueText">
              {dateInfo.day}
            </span>
            <span className="block text-sm text-primaryBlueText -mt-1">
              {dateInfo.month}
            </span>
            <span className="block text-sm text-primaryBlueText -mt-1">
              {dateInfo.year}
            </span>
          </div>
        )}

        {userRole === ROLES.ATTENDEE && showBookmark && (
          <div
            className="absolute top-2 right-2 bg-white text-center p-2 rounded-md"
            onClick={handleToggleWishlist}
          >
            {exp.isWishlisted ? (
              <BookmarkFilled title="Remove from Wishlist" />
            ) : (
              <BookmarkUnfilled title="Add To Wishlist" />
            )}
            {/* {followBtn ? <button>Follow</button> : <button>Unfollow</button>} */}
          </div>
        )}
      </div>

      <div className="">
        <h3 className="text-lg font-semibold text-blackie mt-0.5">
          {exp.title}
        </h3>
        <div className="flex justify-between items-center  w-full my-2">
          <div className="text-sm font-medium text-blackie bg-fade rounded-md px-2 py-1">
            {exp.isSession ? "Session" : "Workshop"}
          </div>
          <div className="text-sm text-darkYellowText">
            {exp.entryFee > 0 ? `$${exp.entryFee}` : "Free"}
          </div>
        </div>
        <p className="text-base text-[#807A7A] flex items-center gap-2">
          <img src={locationIcon} alt="Location" /> {exp.location}
        </p>
      </div>

      {showSupportForExperience && (
        <SupportForExperience
          isOpen={showSupportForExperience}
          onClose={() => {
            setShowSupportForExperience(false);
            onClose();
          }}
          handleConfirm={handleConfirm}
          referralCode={refferalCode}
          eventId={event?.id}
        />
      )}
    </div>
  );
};

export default EventCard;
